import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import type { ReferenceImagesBlockProps } from '../types';
import { InfoIcon } from '../icons';
import { ImagePreviewModal, VideoPreviewModal } from '../../Common/MediaPreviewModal';
import { OptimizedImage } from '../../OptimizedImage';
import styles from './ReferenceImagesBlock.module.css';

// Reference thumbnails render in a 3-4 column grid (~120-200px each).
const REFERENCE_THUMBNAIL_SIZES = '(max-width: 768px) 33vw, 200px';

/**
 * ReferenceImagesBlock - Grid of reference images/videos
 *
 * Displays thumbnails of reference media used in generation.
 * Supports video references (e.g. Runway Aleph input video).
 */
export const ReferenceImagesBlock: React.FC<ReferenceImagesBlockProps> = ({
  images,
  labels,
  isVideoFlags,
  onImageClick,
  onUseReference,
  className,
}) => {
  const { t } = useTranslation('image');
  const [preview, setPreview] = useState<{ url: string; isVideo: boolean; label?: string; index: number } | null>(null);

  const handleImageClick = useCallback((url: string, index: number) => {
    // External callback still fires if parent registered one
    onImageClick?.(url, index);
    setPreview({
      url,
      isVideo: isVideoFlags?.[index] ?? false,
      label: labels?.[index],
      index,
    });
  }, [onImageClick, isVideoFlags, labels]);

  const closePreview = useCallback(() => setPreview(null), []);

  const handleUsePreview = useCallback(() => {
    if (!preview || !onUseReference) return;
    onUseReference(preview.url, preview.isVideo, preview.index);
    setPreview(null);
  }, [preview, onUseReference]);

  if (!images || images.length === 0) {
    return null;
  }

  const hasAnyVideo = isVideoFlags?.some(Boolean);
  const headerText = hasAnyVideo ? t('gallery.refMaterials') : t('gallery.refImages');

  return (
    <>
    <div className={clsx(styles.card, className)}>
      <div className={styles.header}>
        <span className={styles.headerIcon}><InfoIcon /></span>
        <span className={styles.headerLabel}>{headerText}</span>
      </div>

      <div className={styles.grid}>
        {images.map((url, index) => {
          const isVideo = isVideoFlags?.[index] ?? false;

          return (
            <div
              key={`${url}-${index}`}
              className={styles.imageItem}
              onClick={() => handleImageClick(url, index)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleImageClick(url, index);
                }
              }}
            >
              {isVideo ? (
                <video
                  // `#t=0.001` forces most browsers to render the first frame
                  // instead of a blank/black poster when preload=metadata.
                  src={`${url}#t=0.001`}
                  className={styles.image}
                  muted
                  playsInline
                  loop
                  preload="metadata"
                />
              ) : (
                <OptimizedImage
                  src={url}
                  alt={labels?.[index] || `Reference ${index + 1}`}
                  className={styles.image}
                  sizes={REFERENCE_THUMBNAIL_SIZES}
                />
              )}
              {labels?.[index] && (
                <span className={styles.imageLabel}>{labels[index]}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>

    {preview && (preview.isVideo
      ? <VideoPreviewModal
          src={preview.url}
          title={preview.label}
          onClose={closePreview}
          showDownload
          onUse={onUseReference ? handleUsePreview : undefined}
        />
      : <ImagePreviewModal
          src={preview.url}
          alt={preview.label}
          onClose={closePreview}
          showDownload
          onUse={onUseReference ? handleUsePreview : undefined}
        />
    )}
    </>
  );
};

export default ReferenceImagesBlock;
